import {
  App,
  Button,
  Card,
  Col,
  Flex,
  Image,
  Pagination,
  Popconfirm,
  Row,
  Timeline,
  Typography,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  getPhotoListByPatientQueryKey,
  getSubscriptionUsageQueryKey,
  usePhotoListByPatientSuspense,
  usePhotoRemove,
} from '../../../libs/ohat-api';
import { PatientPhotoResponseDto } from '@models/patientPhotoResponseDto';
import { format } from '../../../utils/date';
import { getApiErrorMessage } from '../../../utils/apiError';
import { PatientPhotosPageBreadCrumb } from '../../components/breadcrumb/PatientPhotosPageBreadCrumb';

type PhotoGroup = {
  ohatScoreId: string;
  evaluatedAt: string;
  photos: PatientPhotoResponseDto[];
};

// 1ページに表示する評価（時系列ブロック）の数
const PAGE_SIZE = 5;

export function PatientPhotosPage() {
  const param = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const patientId = param.id ?? '';

  const { data: photos } = usePhotoListByPatientSuspense(patientId);
  const { mutateAsync: removePhoto } = usePhotoRemove();

  const [page, setPage] = useState(1);

  // 評価日時の新しい順に並んだ写真を、評価（ohatScoreId）ごとにまとめる。
  // APIが評価日時降順で返すため、挿入順がそのまま時系列になる。
  const groups = useMemo<PhotoGroup[]>(() => {
    const map = new Map<string, PhotoGroup>();
    for (const p of photos) {
      const group = map.get(p.ohatScoreId);
      if (group) {
        group.photos.push(p);
      } else {
        map.set(p.ohatScoreId, {
          ohatScoreId: p.ohatScoreId,
          evaluatedAt: p.evaluatedAt,
          photos: [p],
        });
      }
    }
    return Array.from(map.values());
  }, [photos]);

  // 削除でグループ数が減ってもページ範囲外にならないよう補正する
  const totalPages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedGroups = groups.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const onDelete = async (photoId: string) => {
    try {
      await removePhoto({ id: photoId });
      await queryClient.invalidateQueries({
        queryKey: getPhotoListByPatientQueryKey(patientId),
      });
      // 月間アップロード数の使用量表示を更新する（削除で枠が戻る）
      queryClient.invalidateQueries({
        queryKey: getSubscriptionUsageQueryKey(),
      });
      message.success(t('photo.deleted'));
    } catch (e) {
      message.error(getApiErrorMessage(e, t('photo.deleteFailed')));
    }
  };

  return (
    <Row gutter={[16, 8]} style={{ flex: 1, maxWidth: 1200 }}>
      <Col md={24} xs={24}>
        <PatientPhotosPageBreadCrumb />
      </Col>
      <Col md={24} xs={24}>
        <Card title={t('patientPhotos.uploaded')}>
          {groups.length === 0 ? (
            <Typography.Text type="secondary">
              {t('patientPhotos.empty')}
            </Typography.Text>
          ) : (
            <>
              <Timeline
                items={pagedGroups.map((group) => ({
                  content: (
                    <Flex vertical gap={8}>
                      <Flex
                        align="center"
                        justify="space-between"
                        wrap="wrap"
                        gap={8}
                      >
                        <Typography.Text strong>
                          {t('patientPhotos.assessmentOf', {
                            date: format(group.evaluatedAt, 'YYYY/MM/DD HH:mm'),
                          })}
                        </Typography.Text>
                        <Button
                          type="link"
                          size="small"
                          onClick={() =>
                            navigate(
                              `/patients/${patientId}/ohat-scores/${group.ohatScoreId}`,
                            )
                          }
                        >
                          {t('patientPhotos.viewAssessment')}
                        </Button>
                      </Flex>
                      <Image.PreviewGroup>
                        <Flex gap={8} wrap="wrap">
                          {group.photos.map((photo) => (
                            <div
                              key={photo.id}
                              style={{ position: 'relative' }}
                            >
                              <Image
                                src={photo.url}
                                alt={t('photo.title')}
                                width={100}
                                height={100}
                                style={{
                                  objectFit: 'cover',
                                  borderRadius: 8,
                                }}
                              />
                              <Popconfirm
                                title={t('common.deleteConfirm')}
                                okText="Yes"
                                cancelText="No"
                                onConfirm={() => onDelete(photo.id)}
                              >
                                <Button
                                  danger
                                  size="small"
                                  shape="circle"
                                  icon={<DeleteOutlined />}
                                  style={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                  }}
                                />
                              </Popconfirm>
                            </div>
                          ))}
                        </Flex>
                      </Image.PreviewGroup>
                    </Flex>
                  ),
                }))}
              />
              {groups.length > PAGE_SIZE && (
                <Flex justify="center" style={{ marginTop: 16 }}>
                  <Pagination
                    current={currentPage}
                    pageSize={PAGE_SIZE}
                    total={groups.length}
                    onChange={setPage}
                    showSizeChanger={false}
                  />
                </Flex>
              )}
            </>
          )}
        </Card>
      </Col>
    </Row>
  );
}
